import { Injectable } from "@angular/core";
import { NavigationEnd, Route, Router } from "@angular/router";
import { BehaviorSubject, filter, Observable } from "rxjs";
import { getRouteChain, isSameRouteChain } from "./route-chain";

@Injectable({ providedIn: 'root' })
export class PageTitleService {
  readonly pageTitle: Observable<string>;

  private _pageTitle = new BehaviorSubject<string>('');

  /** routeConfig chain (root -> leaf) of the last committed navigation. */
  private _routeChain: (Route | null)[] = [];

  constructor(router: Router) {
    this.pageTitle = this._pageTitle.asObservable();

    // Clear the title only when a navigation actually lands on a DIFFERENT page.
    //
    // Two earlier approaches were both wrong:
    //
    //  - Clearing on NavigationStart: a navigation can still be aborted (e.g. a guard
    //    redirect that resolves to the current URL is dropped as a same-URL navigation),
    //    which left the title permanently blank.
    //
    //  - Clearing on every NavigationEnd: the title is written exactly once per page, at the
    //    end of BaseComponent's async updateFromParams(), and that only re-runs when the
    //    route's URL *segments* change -- Angular's advanceActivatedRoute pushes url only on
    //    a segment change, while query params and params get their own subjects. So a
    //    navigation that differs solely in query params, or solely in a *parent* route's
    //    params, reuses the page component: NavigationEnd blanked the title and nothing ever
    //    re-set it. Grid state lands in the URL as query params (see
    //    BaseComponent.updateGridRouteParams), so Back/Forward across grid URLs, a bookmarked
    //    grid URL, or an id-cell nav all hit that case.
    //
    // Comparing the routeConfig chain (see isSameRouteChain) tells us whether the page that
    // owns the current title is still on screen.
    router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      const chain = getRouteChain(router.routerState.snapshot.root);
      const isSamePage = isSameRouteChain(chain, this._routeChain);
      this._routeChain = chain;
      if (!isSamePage) {
        this.setPageTitle('');
      }
    });
  }

  setPageTitle(title: string) {
    this._pageTitle.next(title);
  }

  getPageTitle(): string {
    return this._pageTitle.getValue();
  }
}
